/**
 * Background states and their changes as scene operations (spec 039 D-8):
 * `setBackgroundStates` replaces a background's list of states through the
 * one writer (`setBackgroundStatesOf`, D-1, D-2), and `setBackgroundState`
 * writes one state key on the background track with its transition (D-3),
 * by 036's time rules (seconds, `until` switching back). Pure.
 */
import type { Keyframe, Transition } from '../@types';
import {
  BACKGROUND_STATE_NAME_MAX, BACKGROUND_STATE_PROPERTY, BACKGROUND_TRACK_ID, backgroundStates, backgroundStateSteps, backgroundTrackKeys,
  hasBackgroundStates, setBackgroundStatesOf, withBackgroundTrack,
} from '../utils/backgroundStates';
import { putKeyframe } from '../utils/keyframeWrite';
import { moveBackgroundSlotToState } from '../pictures/pictureMap';
import { KEY_RANGES } from './keyframeOperations';
import { checkTransition, motionKeyframeId, toTransition, TRANSITION_SCHEMA } from './operations';
import { fmtMs, fmtS, secToMs } from './units';
import type { JsonSchema, OperationError, OperationResult, SceneDocument, SceneOperation, SceneTransitionArg } from './types';

const str = (description: string): JsonSchema => ({ type: 'string', description });
const num = (description: string): JsonSchema => ({ type: 'number', description });
const int = (description: string): JsonSchema => ({ type: 'integer', description });
const obj = (properties: Record<string, JsonSchema>, required: string[] = [], description?: string): JsonSchema => ({
  type: 'object', ...(description ? { description } : {}), properties, required, additionalProperties: false,
});

type Errors = OperationError[];
const fail = (errors: Errors): OperationResult => ({ ok: false, errors });
const err = (code: OperationError['code'], message: string, path?: string): OperationError =>
  (path ? { code, message, path } : { code, message });

const statesText = (doc: SceneDocument): string => backgroundStates(doc.background).map((s) => s.name).join(', ') || 'none';

/** `fade 1s`, `wipe left 0.5s`, `cut` (the summaries and `describeScene`). */
export function backgroundTransitionText(t: Transition | undefined): string {
  if (!t || t.type === 'none' || !(t.duration > 0)) return 'cut';
  return `${t.type}${t.direction ? ` ${t.direction}` : ''} ${fmtMs(t.duration)}`;
}

// ─── 19. setBackgroundStates ────────────────────────────────────────────────

export interface SetBackgroundStatesArgs {
  states: { name: string; mediaId?: number }[];
  defaultState?: string;
}

export const setBackgroundStates: SceneOperation<SetBackgroundStatesArgs> = {
  name: 'setBackgroundStates',
  description: 'Give the scene background its states: the places it can show, each its own picture (the beach, the pirate world). Replaces the list; the current picture stays as the state "default" when the background had no states yet. A state dropped from the list loses its changes. Change between states over time with setBackgroundState.',
  inputSchema: obj({
    states: {
      type: 'array',
      minItems: 1,
      description: 'Every state, in order.',
      items: obj({
        name: str(`The place, e.g. pirate-world (1–${BACKGROUND_STATE_NAME_MAX} characters, unique).`),
        mediaId: int('Its image media id. Omit when no picture fits yet: it plays a labelled placeholder until one is filled or generated.'),
      }, ['name']),
    },
    defaultState: str('The state shown from the scene start; default the first (or "default" when the current picture became it).'),
  }, ['states']),
  apply(doc, args, ctx) {
    const errors: Errors = [];
    (args.states ?? []).forEach((s, i) => {
      if (s.mediaId !== undefined && ctx.media && !ctx.media.has(s.mediaId)) {
        errors.push(err('unknown_media', `media ${s.mediaId} is not in the library`, `states[${i}].mediaId`));
      }
    });
    const result = setBackgroundStatesOf(doc, args.states ?? [], args.defaultState);
    if (result.ok === false) {
      errors.push(err(/at most/.test(result.error.message) ? 'out_of_range' : 'invalid_args', result.error.message, result.error.path));
    }
    if (errors.length || result.ok === false) return fail(errors);
    let next = result.doc;
    // A standalone scene's own map (spec 030): the background's slot moves to the default state (D-2, D-6).
    if (!hasBackgroundStates(doc.background) && doc.pictureMap) {
      const moved = moveBackgroundSlotToState(doc.pictureMap.slots, doc.background?.name ?? '', next.background.defaultState!);
      if (moved !== doc.pictureMap.slots) next = { ...next, pictureMap: { ...doc.pictureMap, slots: moved } };
    }
    const list = backgroundStates(next.background).map((s) => `${s.name}${s.mediaId ? ` (image ${s.mediaId})` : ' (no picture)'}`).join(', ');
    const dropped = result.removedKeys > 0 ? `; ${result.removedKeys} change${result.removedKeys === 1 ? '' : 's'} of dropped states removed` : '';
    return {
      ok: true,
      doc: next,
      summary: `Background states: ${list}; default ${next.background.defaultState}${dropped}`,
      createdIds: [],
    };
  },
};

// ─── 20. setBackgroundState ─────────────────────────────────────────────────

export interface SetBackgroundStateArgs {
  state: string;
  at: number;
  until?: number;
  transition?: SceneTransitionArg;
}

const r = (k: keyof typeof KEY_RANGES) => `${KEY_RANGES[k][0]}–${KEY_RANGES[k][1]}`;

export const setBackgroundState: SceneOperation<SetBackgroundStateArgs> = {
  name: 'setBackgroundState',
  description: 'Change the scene background to one of its states at a time (a change of place: fade to the pirate world at 6 s), written as a background state keyframe; transition says how the old picture gives way to the new (cut when omitted); with until it changes back to the state it had before at that time, the same way.',
  inputSchema: obj({
    state: str('One of the background\'s state names (setBackgroundStates gives it states).'),
    at: num(`Seconds from the scene start (${r('time')}).`),
    until: num(`Seconds from the scene start when it changes back (${r('time')}, later than at); omit to keep the state.`),
    transition: TRANSITION_SCHEMA,
  }, ['state', 'at']),
  apply(doc, args) {
    const errors: Errors = [];
    const states = backgroundStates(doc.background);
    const state = states.find((s) => s.name === args.state) ?? states.find((s) => s.name.toLowerCase() === String(args.state ?? '').trim().toLowerCase());
    if (!state) {
      errors.push(err('not_found', states.length
        ? `no state "${args.state}" on the background; its states: ${statesText(doc)}`
        : `the background has no states yet; give it states with setBackgroundStates first`, 'state'));
    }
    const inRange = (v: number | undefined, path: string) => {
      if (v !== undefined && (v < KEY_RANGES.time[0] || v > KEY_RANGES.time[1])) errors.push(err('out_of_range', `${path} must be ${r('time')} s`, path));
    };
    inRange(args.at, 'at');
    inRange(args.until, 'until');
    if (args.until !== undefined && !(args.until > args.at)) errors.push(err('out_of_range', `until must be later than at (${args.at} s)`, 'until'));
    checkTransition(errors, args.transition, 'transition');
    if (errors.length || !state) return fail(errors);

    const transition = args.transition && args.transition.type !== 'none' ? toTransition(args.transition) : undefined;
    const atMs = secToMs(args.at);
    const newId = motionKeyframeId(doc, BACKGROUND_TRACK_ID);
    const key = (time: number, value: string): Keyframe => ({
      id: newId(), time, property: BACKGROUND_STATE_PROPERTY, value, ...(transition ? { transition } : {}),
    });
    let keys = [...backgroundTrackKeys(doc.keyframes)];
    // The state playing just before `at`: the last change before it, else the default.
    const before = backgroundStateSteps(doc.background, doc.keyframes).filter((s) => s.time < atMs).pop();
    const prior = before ? before.to : (doc.background.defaultState && states.some((s) => s.name === doc.background.defaultState) ? doc.background.defaultState : states[0].name);
    if (args.until !== undefined) {
      const untilMs = secToMs(args.until);
      keys = keys.filter((k) => !(k.property === BACKGROUND_STATE_PROPERTY && k.time > atMs && k.time < untilMs));
    }
    keys = putKeyframe(keys, key(atMs, state.name));
    if (args.until !== undefined) keys = putKeyframe(keys, key(secToMs(args.until), prior));
    const when = args.until !== undefined ? ` ${fmtS(args.at)}–${fmtS(args.until)}` : ` at ${fmtS(args.at)}`;
    return {
      ok: true,
      doc: { ...doc, keyframes: withBackgroundTrack(doc.keyframes, keys) },
      summary: `Background: ${state.name}${when} (${backgroundTransitionText(transition)})`,
      createdIds: [],
    };
  },
};
